/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import kotlin.math.abs
import kotlin.math.roundToInt

/**
 * Folds `hr` messages into the `record` messages they overlap.
 *
 * A chest strap samples faster than the once-per-second `record` cadence, so
 * those samples ride in separate `hr` messages as deltas against an anchor
 * rather than as absolute timestamps. Without this step heart rate is simply
 * missing from a file's records even though it was recorded.
 */
public object HrMesgUtil {
    private const val GAP_INCREMENT_MILLISECONDS = 250
    private const val GAP_INCREMENT_SECONDS = GAP_INCREMENT_MILLISECONDS / 1000.0
    private const val GAP_MAX_MILLISECONDS = 5000
    private const val GAP_MAX_STEPS = GAP_MAX_MILLISECONDS / GAP_INCREMENT_MILLISECONDS

    /** The event timestamp counter is 22 bits wide and wraps. */
    private const val EVENT_TIMESTAMP_ROLLOVER = 4194304.0

    private data class Sample(val timestamp: Double, val heartRate: UByte)

    /**
     * Sets [RecordMesg.heartRate] on each record to the mean of the samples
     * falling in its interval.
     *
     * Does nothing when either list is empty, and gives up quietly on an `hr`
     * stream it cannot anchor: a malformed heart rate section should not cost
     * the caller the rest of a decoded activity.
     */
    public fun mergeHeartRates(hrMesgs: List<HrMesg>, recordMesgs: List<RecordMesg>) {
        if (hrMesgs.isEmpty() || recordMesgs.isEmpty()) return

        val samples = expandHeartRates(hrMesgs) ?: return
        if (samples.isEmpty()) return

        var sampleIndex = 0
        var rangeStart: Double? = null

        for (record in recordMesgs) {
            val rangeEnd = (record.getFieldValue(RecordMesg.TIMESTAMP_FIELD_NUM) as? UInt)?.toDouble() ?: continue

            var start = rangeStart ?: rangeEnd
            if (start == rangeEnd) {
                // The first record has no predecessor to bound it, so give it a
                // one-second window and reconsider the previous sample.
                start -= 1.0
                if (sampleIndex >= 1) sampleIndex -= 1
            }
            rangeStart = start

            var sum = 0
            var count = 0

            while (sampleIndex < samples.size) {
                val sample = samples[sampleIndex]

                if (sample.timestamp > start && sample.timestamp <= rangeEnd) {
                    sum += sample.heartRate.toInt()
                    count++
                } else if (sample.timestamp > rangeEnd) {
                    if (count > 0) {
                        record.heartRate = (sum.toDouble() / count).roundToInt().toUByte()
                    }
                    rangeStart = rangeEnd
                    // Leave sampleIndex where it is: this sample belongs to the
                    // next record's window.
                    break
                }
                sampleIndex++
            }
        }
    }

    /**
     * Turns the delta-encoded `hr` stream into absolute `(timestamp, bpm)`
     * samples, or null when the stream cannot be anchored or is inconsistent.
     */
    private fun expandHeartRates(hrMesgs: List<HrMesg>): List<Sample>? {
        var anchorEventTimestamp: Double? = 0.0
        var anchorTimestamp: Double? = null
        val samples = mutableListOf<Sample>()

        for (hrMesg in hrMesgs) {
            val eventTimestamps = hrMesg.eventTimestamp ?: emptyList()
            val filteredBpms = hrMesg.filteredBpm ?: emptyList()

            val timestamp = hrMesg.getFieldValue(HrMesg.TIMESTAMP_FIELD_NUM) as? UInt
            if (timestamp != null) {
                anchorTimestamp = timestamp.toDouble() + (hrMesg.fractionalTimestamp ?: 0.0)

                // An anchoring message must pin exactly one event timestamp;
                // anything else leaves the deltas ambiguous.
                if (eventTimestamps.size != 1) return null
                anchorEventTimestamp = eventTimestamps[0]
            }

            if (anchorTimestamp == null || anchorEventTimestamp == null) return null
            if (eventTimestamps.size != filteredBpms.size) return null

            for ((index, rawEventTimestamp) in eventTimestamps.withIndex()) {
                var eventTimestamp = rawEventTimestamp ?: return null
                val bpm = filteredBpms[index] ?: return null

                if (eventTimestamp < anchorEventTimestamp) {
                    if ((anchorEventTimestamp - eventTimestamp) > EVENT_TIMESTAMP_ROLLOVER) {
                        eventTimestamp += EVENT_TIMESTAMP_ROLLOVER
                    } else {
                        return null
                    }
                }

                val sample = Sample(anchorTimestamp + (eventTimestamp - anchorEventTimestamp), bpm)

                // Carry the previous reading forward across a gap, in 250 ms
                // steps for up to 5 s, so a dropout does not read as a sudden
                // absence of heart rate.
                samples.lastOrNull()?.let { previous ->
                    var gapMs = abs(sample.timestamp - previous.timestamp) * 1000
                    var step = 1
                    while (gapMs > GAP_INCREMENT_MILLISECONDS && step <= GAP_MAX_STEPS) {
                        samples.add(Sample(previous.timestamp + GAP_INCREMENT_SECONDS * step, previous.heartRate))
                        gapMs -= GAP_INCREMENT_MILLISECONDS
                        step++
                    }
                }

                samples.add(sample)
            }
        }

        return samples
    }
}
